/**
 * DuplicateCourseDialog
 *
 * Client-side "duplicate this course" flow. The backend has no
 * single-call clone endpoint, so this stitches the existing CRUD
 * primitives together:
 *
 *   1. Re-fetch the source course in full via `getCourseById` — the
 *      row payload in the admin table is a slim listing and is
 *      missing detail-tab content, video pointers, etc.
 *   2. Build an `AddCourseRequest` from the source, swapping in the
 *      new title and forcing `isDraft = true` so the duplicate doesn't
 *      accidentally appear in learner catalogs before the admin is
 *      done editing it.
 *   3. `coursesClient.addCourse(payload)` → new course id.
 *   4. Walk the source's lessons in order and `addLesson` each one
 *      against the new course. Mux pointers are copied verbatim, so
 *      videos point at the same Mux asset (cheap; no re-upload). Per-
 *      lesson failures don't abort the rest of the loop — the dialog
 *      surfaces a partial-success warning instead of leaving a
 *      half-built course with nothing the admin can recover.
 *
 * On success the parent fires its list refresh callback and the
 * dialog hands the user the new course's slug so it can optionally
 * navigate them straight into the edit page.
 */

import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import {
  AddCourseRequest,
  AddLessonRequest,
  Course,
  DetailTabItem,
  PathCourseItem,
  Tag,
  TagType,
} from '../../apis/api-client';
import { coursesClient, lessonsClient } from '../../apis/client-instances';
import { alert } from '../../hooks/function';
import TagAutocomplete from '../common/TagAutocomplete';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '../ui';

export interface DuplicateCourseDialogProps {
  open: boolean;
  source: Course | null;
  onClose: () => void;
  /** Fires after a successful duplicate so the parent can refresh
   *  the table. Receives the newly-created course (with its id +
   *  slug) so the parent can route to its edit page if it wants. */
  onDuplicated?: (created: Course) => void;
}

export default function DuplicateCourseDialog({
  open,
  source,
  onClose,
  onDuplicated,
}: DuplicateCourseDialogProps) {
  const { enqueueSnackbar } = useSnackbar();
  const [title, setTitle] = useState('');
  // Content tags drive which workspaces the course shows up in, so we
  // surface them in the duplicate flow — admins want to re-route the
  // copy to a different workspace set without having to open the new
  // course's edit page afterwards. Defaults to the source's tags.
  const [selectedTags, setSelectedTags] = useState<Tag[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Seed/reset the form each time the dialog opens. Title defaults to
  // the source title with " copy" appended — the admin almost always
  // wants to tweak it, but the prefilled value makes the common case
  // a single Enter. Tags prefill from `source.tags` if the row carries
  // them; if the row was a slim listing without the join rows hydrated
  // the picker starts empty and the admin can pick fresh.
  useEffect(() => {
    if (open && source) {
      setTitle(`${source.title ?? 'Untitled'} copy`);
      setSelectedTags((source.tags ?? []).filter((t) => !!t?.id));
      setError(null);
    }
  }, [open, source]);

  const handleClose = () => {
    if (submitting) return;
    onClose();
  };

  const handleSubmit = async () => {
    if (!source?.id) return;
    const trimmed = title.trim();
    if (!trimmed) {
      setError('Title is required');
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      // Re-fetch in full — the row payload is a listing snapshot and
      // may be missing detail tabs / video pointers we want to clone.
      const full = await coursesClient.getCourseById(source.id);

      // Tag ids come from the in-dialog picker — the admin may have
      // edited them to re-route the duplicate to a different workspace
      // set. An empty selection intentionally clears tags on the copy
      // (account-wide / no workspace routing).
      const tagIds = selectedTags
        .map((t) => t.id)
        .filter((id): id is number => typeof id === 'number');

      const payload: AddCourseRequest = {
        accountId: full.accountId,
        title: trimmed,
        thumbnail: full.thumbnail,
        description: full.description,
        tagIds,
        // Copies always start as drafts so the admin can review/edit
        // before exposing them to learners.
        isDraft: true,
        enforceLinearProgress: full.enforceLinearProgress,
        isPublic: full.isPublic,
        courseType: full.courseType,
        externalUrl: full.externalUrl,
        eventDate: full.eventDate,
        // Learning-path child courses round-trip as `{courseId, sequenceOrder}`
        // — strip the join-row id so the new path gets fresh rows.
        pathCourses: full.pathCourses?.map<PathCourseItem>((pc) => ({
          courseId: pc.courseId,
          sequenceOrder: pc.sequenceOrder,
        })),
        detailImageUrl: full.detailImageUrl,
        detailImageAltText: full.detailImageAltText,
        detailVideoProvider: full.detailVideo?.provider,
        detailVideoExternalId:
          full.detailVideo?.externalId ?? full.detailVideo?.muxPlaybackId,
        // Strip the source tab ids so the server creates fresh rows
        // pointing at the new course, not the source's.
        detailTabs: full.detailTabs?.map<DetailTabItem>((t) => ({
          title: t.title,
          contentHtml: t.contentHtml,
        })),
      };

      const created = await coursesClient.addCourse(payload);

      // Touch the timestamp. The backend's `addCourse` doesn't populate
      // `updatedAt` on the new row — it comes back as DateTime.MinValue
      // (renders as "1/1/1") — and the admin table defaults to sorting
      // updated-desc, which would bury the new draft at the bottom of
      // the list and look broken. Firing an immediate `updateCourse`
      // with the same payload bumps `updatedAt` to now so the
      // duplicate lands at the top. Failures here aren't fatal — the
      // course still exists, just with a stale timestamp.
      if (created?.id) {
        try {
          await coursesClient.updateCourse(created.id, payload);
        } catch (e) {
          console.warn('Failed to touch updatedAt on duplicated course', e);
        }
      }

      // Clone the lessons. Sequential rather than Promise.all so a
      // single failure doesn't leave us guessing which calls landed
      // — we just stop counting succeeded and report what we managed.
      let lessonOk = 0;
      let lessonFail = 0;
      try {
        const sourceLessons = await lessonsClient.getLessonsByCourse(source.id);
        // The list endpoint doesn't guarantee order, so sort by
        // `sequenceOrder` to preserve the curriculum's intent.
        const ordered = [...(sourceLessons ?? [])].sort(
          (a, b) => (a.sequenceOrder ?? 0) - (b.sequenceOrder ?? 0),
        );
        for (const l of ordered) {
          const lessonReq: AddLessonRequest = {
            accountId: l.accountId,
            title: l.title,
            lessonType: l.lessonType,
            description: l.description,
            thumbnail: l.thumbnail,
            // Note the source typo on the request DTO (`videUrl`) —
            // backend-generated. Keeping it as-is so the field reaches
            // the server.
            videUrl: l.videoUrl,
            attachedUrl: l.attachedUrl,
            file: l.file,
            textContent: l.textContent,
            moduleId: l.moduleId,
            videoProvider: l.videoProvider,
            externalVideoId: l.externalVideoId ?? l.muxPlaybackId,
            quizSettings: l.quizSettings,
          };
          try {
            await lessonsClient.addLesson(lessonReq, created.id!);
            lessonOk += 1;
          } catch (e) {
            console.warn('Failed to clone lesson', l.id, e);
            lessonFail += 1;
          }
        }
      } catch (e) {
        console.warn('Failed to read source lessons for duplication', e);
      }

      if (lessonFail > 0) {
        alert(
          enqueueSnackbar,
          `Course duplicated with ${lessonOk} lessons; ${lessonFail} failed to copy.`,
          'warning',
        );
      } else if (lessonOk > 0) {
        alert(
          enqueueSnackbar,
          `Course duplicated with ${lessonOk} ${lessonOk === 1 ? 'lesson' : 'lessons'}.`,
          'success',
        );
      } else {
        alert(enqueueSnackbar, 'Course duplicated', 'success');
      }

      onDuplicated?.(created);
      onClose();
    } catch (e: any) {
      const msg = e?.response?.data || e?.message || 'Failed to duplicate course';
      setError(typeof msg === 'string' ? msg : 'Failed to duplicate course');
      alert(enqueueSnackbar, typeof msg === 'string' ? msg : 'Failed to duplicate course', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="h6" fontWeight={700}>
          Duplicate course
        </Typography>
        {source?.title && (
          <Typography variant="caption" color="text.secondary">
            Source: {source.title}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField
            autoFocus
            label="New course title"
            fullWidth
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            // Enter submits only from the title field. The tag picker
            // below uses Enter to commit a selected option, so we let
            // it handle its own keyboard.
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !submitting) handleSubmit();
            }}
            error={!!error}
          />

          {/* Content tag picker — drives workspace routing for the
              new course. Restricted to CourseTag type so user tags
              don't clutter the list. Defaults to the source's tags;
              admins can swap them out here to route the duplicate
              to a different set of workspaces in one step. */}
          <TagAutocomplete
            multiple
            value={selectedTags}
            onChange={(v) => setSelectedTags((v as Tag[]) ?? [])}
            tagType={TagType.CourseTag}
            size="medium"
            fullWidth
            disabled={submitting}
            label="Content tags"
            placeholder="Search course tags…"
            helperText="Tags determine which workspaces the duplicate appears in. Defaults to the source's tags — change here to route the copy elsewhere."
          />

          <Typography variant="caption" color="text.secondary">
            The copy starts as a draft. Lessons, detail tabs, and learning-path
            children are copied; Mux videos are shared by reference.
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} color="inherit" disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={submitting || !title.trim()}
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {submitting ? 'Duplicating…' : 'Duplicate'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
